import React, { useCallback, useContext, memo } from 'react';
import { useHistory, useLocation } from 'react-router';

// Context
import MobileContext from 'Shared/contexts/MobileContext';

// Hooks
import { useToggle, useDeepEqualSelector } from 'Shared/utils/hooks';

// Types
import { SourcePage } from 'Shared/types/SourcePage.type';
import { ItemType } from 'Shared/constants/types';

// Selectors
import { getMeetingItemByUuid } from 'Shared/selectors/common';

// Utils
import { handleLaunchExternalLink } from 'Shared/utils/external-link';
import { getSourcePage } from 'Shared/utils/getSourcePage';
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';

// Components
import { WpButtonGroup } from 'Wpds/components/button';
import BaseButton from './partials/BaseButton';
import RecurringItemModal from 'Shared/components/modals/RecurringItemModal/RecurringItemModal';
import GoalDropdown from 'Shared/components/WorkspaceItemContainer/ActionsArea/ActionsAreaForDesktop/GoalButton/GoalDropdown/GoalDropdown';
import ReviewDropdown from 'Shared/components/WorkspaceItemContainer/ActionsArea/ActionsAreaForDesktop/ReviewButton/ReviewDropdown/ReviewDropdown';

type Props = {
  uuid: string;
};

const MeetingItemIndicators = ({ uuid }: Props) => {
  const location = useLocation();
  const history = useHistory();
  const isMobile = useContext(MobileContext);

  const isGoalsDetailsPage = getSourcePage() == SourcePage.goalDetailPage;
  const isReviewsDetailsPage = getSourcePage() == SourcePage.reviewDetailPage;
  const isMeetingModePage = getSourcePage() == SourcePage.meetingMode;

  const [isEditRecurrenceModalOpen, toggleIsRecurrenceModalOpen] = useToggle(false);

  const {
    itemType,
    isSection,
    commentsCount,
    recurringWorkspaceItem,
    jiraData,
    asanaData,
    associatedActionItemsCount,
    associatedMeetingItemUuid,
    reviewSlug,
    goalIcon,
    goalName,
    goalOwner,
    goalSlug,
  } = useDeepEqualSelector(state => getMeetingItemByUuid(uuid)(state));

  const isActionItem = itemType === ItemType.actionItem;
  const isDiscussionTopic = itemType === ItemType.discussionTopic;

  const handleOpenDetailsPanel = useCallback(e => {
    e.stopPropagation(0);
    let path = location.pathname;
    // need to check and see if the panel is already open - if so, we need
    // strip the existing uuid off the route before appending the new one
    if (path.includes('/item/')) {
      path = path.split('/item/')[0];
    }

    // check for trailing slash
    path = path.slice(-1) === '/' ? path.slice(0, -1) : path;

    history.push({
      pathname: `${path}/item/${uuid}/comments`,
      state: { modal: location.state?.modal, preventPreviousRouteChange: location.state?.modal },
    });
  }, []);

  const handleOpenJira = e => {
    e.stopPropagation(0);
    (window as any).analytics.track('Clicked item', { button: 'Open in Jira' });
    handleLaunchExternalLink({ href: jiraData.url });
  };

  const handleOpenAsana = e => {
    e.stopPropagation(0);
    (window as any).analytics.track('Clicked item', { button: 'Open in Asana' });
    handleLaunchExternalLink({ href: asanaData.url });
  };

  const navigateToReview = () => {
    history.push({
      pathname: PageUrlsUtils.makeReviewShareUrl(reviewSlug),
      state: { modal: true },
    });
  };

  const handleNavigateToReviewClick = useCallback(
    e => {
      e.stopPropagation(0);
      navigateToReview();
    },
    [reviewSlug]
  );

  const navigateToGoal = () => {
    history.push({
      pathname: PageUrlsUtils.makeGoalDetailPageUrl(goalSlug),
      state: { modal: true },
    });
  };

  const handleNavigateToGoalClick = useCallback(
    e => {
      e.stopPropagation(0);
      navigateToGoal();
    },
    [goalSlug]
  );

  if (isMobile) return null;

  return (
    <>
      <WpButtonGroup spacing="sm">
        {isActionItem && recurringWorkspaceItem && (
          <BaseButton icon="recycle" tooltip="Recurring Item" onClick={toggleIsRecurrenceModalOpen} />
        )}
        {isDiscussionTopic && recurringWorkspaceItem && (
          <BaseButton icon="recycle" tooltip="Revisit Item" onClick={handleOpenDetailsPanel} />
        )}
        {isActionItem && reviewSlug && !isReviewsDetailsPage && (
          <BaseButton
            icon="atom-alt"
            onClick={handleNavigateToReviewClick}
            customDropdown={<ReviewDropdown reviewSlug={reviewSlug} clickViewDetails={navigateToReview} />}
          />
        )}
        {isActionItem && goalSlug && !isGoalsDetailsPage && (
          <BaseButton
            icon="bullseye-arrow"
            onClick={handleNavigateToGoalClick}
            customDropdown={
              <GoalDropdown
                goalIcon={goalIcon}
                goalName={goalName}
                goalOwner={goalOwner}
                clickViewDetails={navigateToGoal}
              />
            }
          />
        )}
        {!isSection && jiraData && (
          <BaseButton iconPrefix="fab" icon="jira" tooltip="Open in Jira" onClick={handleOpenJira} />
        )}
        {!isSection && asanaData && (
          <BaseButton iconPrefix="fac" icon="asana" tooltip="Open in Asana" onClick={handleOpenAsana} />
        )}
        {isDiscussionTopic && !isSection && associatedActionItemsCount > 0 && (
          <BaseButton
            icon="check-square"
            count={associatedActionItemsCount}
            tooltip={`${associatedActionItemsCount} linked action items`}
            onClick={handleOpenDetailsPanel}
          />
        )}
        {isActionItem && associatedMeetingItemUuid && !isMeetingModePage && (
          <BaseButton icon="comment-alt-check" tooltip="Linked Discussion Topic" onClick={handleOpenDetailsPanel} />
        )}
        {!isSection && commentsCount > 0 && (
          <BaseButton
            icon="comment"
            count={commentsCount}
            tooltip="View comments"
            onClick={handleOpenDetailsPanel}
            qaTest="MeetingItemIndicators-comment-btn"
          />
        )}
      </WpButtonGroup>
      {isEditRecurrenceModalOpen && <RecurringItemModal itemUuid={uuid} onToggle={toggleIsRecurrenceModalOpen} />}
    </>
  );
};

export default memo(MeetingItemIndicators);
